import { Locale } from 'date-fns'
import { format, OptionsWithTZ, utcToZonedTime } from 'date-fns-tz'
import intl from 'react-intl-universal'
import { LocaleUtils as DPLocaleUtils } from 'react-day-picker'

let localeStr: string
let _locale: Locale | null

/**
 * date-fns types do not include specific type info for many of the Locale properties
 */
interface FormatOpts {
  width: 'short' | 'medium' | 'long' | 'full'
}

interface LocaleExt extends Locale {
  formatLong: {
    date: (options: FormatOpts) => string
    time: (options: FormatOpts) => string
    dateTime: (options: FormatOpts) => string
  }
}

/**
 * Since 12/24 hour is a global setting, we can't just rely on the locale settings.
 * These values are taken from regions that use 12/24 hours
 * https://github.com/date-fns/date-fns/blob/master/src/locale/en-US/_lib/formatLong/index.js
 * https://github.com/date-fns/date-fns/blob/master/src/locale/de/_lib/formatLong/index.js
 */
export const TIME_FORMATS = {
  hour24: 'HH:mm',
  hour12: 'h:mm a',
}

/**
 * Provides utilities for working with locales and timezones
 */
class LocaleUtils {
  private tz: string

  public set timeZone(newTz: string) {
    // Global Settings currently returns deprecated values not recognized by
    // date-fns-tz, so need to get connonical IANA values
    const IANAMap = {
      'US/Alaska': 'America/Anchorage',
      'US/Arizona': 'America/Phoenix',
      'US/Central': 'America/Chicago',
      'US/Eastern': 'America/New_York',
      'US/Mountain': 'America/Denver',
      'US/Pacific': 'America/Los_Angeles',
    }
    if (Object.keys(IANAMap).find((tz) => tz === newTz)) {
      this.tz = IANAMap[newTz]
    } else {
      this.tz = newTz
    }
  }

  public constructor() {
    this.getLocale()
    // default to the browser's timezone
    this.tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  }

  /**
   * Loads locale object based on user's current locale
   */
  private async getLocale() {
    let fromLocales: Promise<{ default: Locale }>
    if (_locale) return _locale

    localeStr = intl.determineLocale({})
    switch (localeStr) {
      case 'de':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-de' */ 'date-fns/locale/de'
        )
        break
      case 'es':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-es' */ 'date-fns/locale/es'
        )
        break
      case 'fr':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-fr' */ 'date-fns/locale/fr'
        )
        break
      case 'ja':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-ja' */ 'date-fns/locale/ja'
        )
        break
      case 'ko':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-ko' */ 'date-fns/locale/ko'
        )
        break
      case 'nb':
      case 'no':
        // 'no' is not supported by the library, so it falls to the most common Norwegian locale
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-nb' */ 'date-fns/locale/nb'
        )
        break
      case 'nn':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-nn' */ 'date-fns/locale/nn'
        )
        break
      case 'sv':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-sv' */ 'date-fns/locale/sv'
        )
        break
      case 'en-NZ':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-enNZ' */ 'date-fns/locale/en-NZ'
        )
        break
      case 'ar':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          // WARNING: date-fns does not support the full locale code 'ar', so for now we're using 'ar-SA'
          /* webpackChunkName: 'dateFns-locale-ar' */ 'date-fns/locale/ar-SA'
        )
        break
      case 'pt':
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-pt' */ 'date-fns/locale/pt'
        )
        break
      case 'en-US':
      default:
        // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
        // @ts-ignore
        fromLocales = import(
          /* webpackChunkName: 'dateFns-locale-enUS' */ 'date-fns/locale/en-US'
        )
    }
    _locale = await fromLocales.then(({ default: utils }) => utils)
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore
    _locale.code = localeStr // `code` requried for date-fns-tz
    return _locale
  }

  /** USE ONLY FOR TESTING */
  public async updateLocale() {
    _locale = null
    await this.getLocale()
  }

  /**
   * Passes all params to date-fns format method with current locale
   * @see date-fns-tz/format https://github.com/marnusw/date-fns-tz#format
   * @returns string of formatted date. i.e. 'Jan 29, 2021 - 5:30 PM'
   */
  public format(
    date: Date | number | string,
    formatStr: string,
    options?: OptionsWithTZ
  ) {
    return format(utcToZonedTime(date, this.tz), formatStr, {
      ...options,
      timeZone: this.tz,
      locale: this.locale,
      ...(this.locale.options && {
        weekStartsOn: this.locale.options.weekStartsOn,
        firstWeekContainsDate: this.locale.options.firstWeekContainsDate,
      }),
    })
  }

  /**
   * Unlike format, cleanFormat formats incoming date without
   * adjusting to the org timezone (useful if this has already occurred)
   */

  public cleanFormat(
    date: Date | number,
    formatStr: string,
    options?: OptionsWithTZ
  ) {
    return format(date, formatStr, {
      ...options,
      locale: this.locale,
      ...(this.locale.options && {
        weekStartsOn: this.locale.options.weekStartsOn,
        firstWeekContainsDate: this.locale.options.firstWeekContainsDate,
      }),
    })
  }

  /**
   * Provides a new date object adjusted to the org timezone, eg 1PM LA -> 4PM NYC
   * NOTE: Use carefully! It only adjusts date value, not the rest of the object.
   */

  public relativeNow() {
    return utcToZonedTime(new Date(), this.tz)
  }

  /**
   * Shifts the time of the provided date to the relative org timezone.
   * NOTE: Use carefully! It only adjusts date value, not the rest of the object.
   */

  public relativeDate(date: Date) {
    return utcToZonedTime(date, this.tz)
  }

  /**
   * Determine if this locale uses 12 or 24 time format. Unfortunately the Locale
   * doesn't share this information, but we can use the format string to see if the
   * am/pm flag ('a') is part of the string.
   */
  public get use24Hours(): boolean {
    return this.locale.formatLong.time({ width: 'short' }).includes('a')
  }

  /**
   * A Locale object https://date-fns.org/v2.0.0-alpha.27/docs/Locale
   * @returns Locale for the user's language setting (en, de, fr, es)
   */
  public get locale(): LocaleExt {
    return _locale as LocaleExt
  }

  /**
   * Utilities used in react-day-picker based on current locale
   * @returns LocaleUtils of react-day-picker
   */
  public get localeUtils(): DPLocaleUtils {
    const utils = {
      formatDay: (day: Date) => day.toDateString(),
      formatMonthTitle: (date: Date) => this.format(date, 'MMMM yyyy'),
      formatWeekdayShort: (day: number) =>
        this.locale.localize &&
        this.locale.localize.day(day, { width: 'short' }),
      formatWeekdayLong: (day: number) =>
        this.locale.localize &&
        this.locale.localize.day(day, { width: 'wide' }),
      getFirstDayOfWeek: () =>
        this.locale.options ? this.locale.options.weekStartsOn : 0, // en-US default
      getMonths: () =>
        [...Array(12).keys()].map(
          (m) =>
            this.locale.localize &&
            this.locale.localize.month(m, { width: 'wide' })
        ),
    }
    return utils as DPLocaleUtils
  }
}

export default new LocaleUtils()
